import { type PaletteColor } from './Colors';

export const brand = {
  'brand-100': '#0C595C',
};

export const teal = {
  'teal-50': '#E8FAF5',
  'teal-100': '#C5F2E7',
  'teal-300': '#4BC7B4',
  'teal-400': '#55B6A3',
  'teal-500': '#00AB93',
  'teal-600': '#459B86',
  'teal-700': '#008D75',
  'teal-800': '#357A67',
  'teal-900': '#275D4A',
};

export const blue = {
  'blue-50': '#F5FAFF',
  'blue-100': '#D8EAFD',
  'blue-200': '#CDE4FD',
  'blue-300': '#6FB2F9',
  'blue-400': '#51A1F8',
  'blue-500': '#4090F7',
  'blue-600': '#1183FF',
  'blue-700': '#216FF7',
  'blue-800': '#285DE4',
  'blue-900': '#2E39C4',
  'blue-950': '#0B1724',
};

export const red = {
  'red-50': '#FFC9C9',
  'red-100': '#FEADAF',
  'red-200': '#FF8A8A',
  'red-300': '#FB7373',
  'red-400': '#EC5D55',
  'red-500': '#FF3D30',
  'red-600': '#EA483D',
  'red-700': '#EB272B',
  'red-800': '#CC3630',
  'red-900': '#BD2C25',
};

export const orange = {
  'orange-40': '#FFFCF8',
  'orange-50': '#FCF1D9',
  'orange-100': '#FADC99',
  'orange-200': '#F8CE76',
  'orange-300': '#F6BF52',
  'orange-400': '#F5B83F',
  'orange-500': '#F3A800',
  'orange-600': '#E8AB3B',
  'orange-700': '#D79927',
  'orange-800': '#A57025',
  'orange-900': '#84571B',
};

export const gray = {
  'gray-50': '#FFFFFF',
  'gray-100': '#FAFBFA',
  'gray-200': '#F9FAFB',
  'gray-300': '#F5F6F7',
  'gray-400': '#ECEEF0',
  'gray-500': '#D8DDE1',
  'gray-600': '#A8B3BC',
  'gray-700': '#7E8C98',
  'gray-800': '#4A5660',
  'gray-900': '#2E373E',
  'gray-1000': '#000000',
} as const;

export const green = {
  'green-50': '#F8FFFA',
  'green-100': '#E0F5E6',
  'green-150': '#D9FCE0',
  'green-200': '#C4EDD0',
  'green-300': '#ADE6BB',
  'green-600': '#3EAE5E',
  'green-700': '#32C055',
  'green-800': '#257A3E',
  'green-900': '#1B5E2F',
  'green-1000': '#16412B',
};

export const purple = {
  'purple-25': '#F5F3FC',
  'purple-50': '#EBE6F9',
  'purple-100': '#E1DAF6',
  'purple-200': '#D0C5F1',
  'purple-300': '#B7A6EA',
  'purple-600': '#6646DD',
  'purple-700': '#4926CC',
  'purple-800': '#391E9F',
};

export const aqua = {
  'aqua-150': '#C5F3FF',
  'aqua-300': '#9BD9DB',
  'aqua-600': '#38B3B9',
  'aqua-800': '#20666A',
};

export const tangerine = {
  'tangerine-300': '#F4A63A',
  'tangerine-500': '#F26C56',
  'tangerine-650': '#C45100',
  'tangerine-700': '#B94634',
};

export const brown = {
  'brown-25': '#FAF7F4',
  'brown-100': '#E7D7CA',
  'brown-300': '#D6BAA4',
  'brown-400': '#CBA485',
  'brown-500': '#C1916C',
  'brown-600': '#AE764A',
  'brown-800': '#68462C',
};

export const warmgray = {
  'warmgray-300': '#BCBCBC',
  'warmgray-700': '#7A7A7A',
};

export const aliceblue = {
  'aliceblue-25': '#F8FAFF',
};

export const magenta = {
  'magenta-25': '#FFFAFE',
  'magenta-50': '#FFF0FB',
  'magenta-100': '#FFE0F8',
  'magenta-200': '#FFB2ED',
  'magenta-300': '#FF85E2',
  'magenta-400': '#FF42D3',
  'magenta-500': '#FF04C4',
  'magenta-600': '#D100A0',
  'magenta-700': '#9E0079',
  'magenta-800': '#6B0052',
  'magenta-900': '#570042',
};

// Brand theme primitives, mirrored from the "_Brand / Primitives" collection in
// Kizen DS v3.1. Figma's `teal-*` ramp is named `deepteal-*` here because the
// legacy `teal-*` palette above already owns those keys in the flat palette
// namespace. Every other family keeps its Figma name.

export const deepteal = {
  'deepteal-50': '#E8FBFC',
  'deepteal-100': '#E1F5F5',
  'deepteal-150': '#D9ECED',
  'deepteal-200': '#CFE2E3',
  'deepteal-250': '#C4D7D8',
  'deepteal-300': '#B9CCCC',
  'deepteal-350': '#AAC0C0',
  'deepteal-400': '#96B5B6',
  'deepteal-450': '#84AAAB',
  'deepteal-500': '#709D9F',
  'deepteal-550': '#5D9193',
  'deepteal-600': '#4A8486',
  'deepteal-650': '#39777A',
  'deepteal-700': '#25696C',
  'deepteal-750': '#0C595C',
  'deepteal-800': '#004B4E',
  'deepteal-850': '#003B3D',
  'deepteal-900': '#002A2C',
  'deepteal-950': '#001819',
};

export const lightmist = {
  'lightmist-50': '#F4F9F7',
};

export const neutral = {
  'neutral-0': '#FFFFFF',
  'neutral-25': '#FBFBFB',
  'neutral-50': '#F3F4F6',
  'neutral-75': '#EFF0F2',
  'neutral-100': '#EBEDEF',
  'neutral-150': '#E3E4E8',
  'neutral-200': '#DBDEE3',
  'neutral-250': '#D4D8DD',
  'neutral-300': '#C4C7CE',
  'neutral-350': '#B4B7BF',
  'neutral-400': '#A3A8B0',
  'neutral-450': '#9499A1',
  'neutral-500': '#858A93',
  'neutral-550': '#767B85',
  'neutral-600': '#686D77',
  'neutral-650': '#5A5F69',
  'neutral-700': '#4B525C',
  'neutral-750': '#3F454E',
  'neutral-800': '#343941',
  'neutral-850': '#292D34',
  'neutral-900': '#1E2227',
  'neutral-950': '#101419',
  'neutral-1000': '#000000',
};

export const yolk = {
  'yolk-50': '#FFF5EA',
  'yolk-100': '#FFEDD9',
  'yolk-150': '#FCE3C8',
  'yolk-200': '#FFD6A7',
  'yolk-250': '#FFC784',
  'yolk-300': '#FFB75B',
  'yolk-350': '#F4A63A',
  'yolk-400': '#E89B2C',
  'yolk-450': '#DA8F1B',
  'yolk-500': '#CA8201',
  'yolk-550': '#B87600',
  'yolk-600': '#A66A00',
  'yolk-650': '#955E00',
  'yolk-700': '#825200',
  'yolk-750': '#6F4500',
  'yolk-800': '#5C3800',
  'yolk-850': '#482C04',
  'yolk-900': '#322009',
  'yolk-950': '#1F1000',
};

export const clay = {
  'clay-50': '#FFF4F2',
  'clay-100': '#FFEBE7',
  'clay-150': '#FFE0D9',
  'clay-200': '#FED2CA',
  'clay-250': '#FFC2B5',
  'clay-300': '#FFB1A1',
  'clay-350': '#FF9C8A',
  'clay-400': '#FF8671',
  'clay-450': '#F26C56',
  'clay-500': '#E9644E',
  'clay-550': '#D85742',
  'clay-600': '#C64A37',
  'clay-650': '#B43F2D',
  'clay-700': '#9F3424',
  'clay-750': '#892B1D',
  'clay-800': '#712317',
  'clay-850': '#581C13',
  'clay-900': '#3E160F',
  'clay-950': '#260905',
};

export const petrol = {
  'petrol-50': '#F0F8FF',
  'petrol-100': '#E4F2FF',
  'petrol-150': '#D9EAF9',
  'petrol-200': '#D0E0EF',
  'petrol-250': '#C4D5E4',
  'petrol-300': '#B9CAD8',
  'petrol-350': '#A8BED1',
  'petrol-400': '#95B2CB',
  'petrol-450': '#83A6C5',
  'petrol-500': '#6F99BC',
  'petrol-550': '#5D8BB3',
  'petrol-600': '#4C7EA8',
  'petrol-650': '#3B719C',
  'petrol-700': '#245C87',
  'petrol-750': '#22537B',
  'petrol-800': '#1D4465',
  'petrol-850': '#183550',
  'petrol-900': '#122539',
  'petrol-950': '#0B1724',
};

export const indigo = {
  'indigo-50': '#F4F6FF',
  'indigo-100': '#ECEFFF',
  'indigo-150': '#E0E6FF',
  'indigo-200': '#D7DCF9',
  'indigo-250': '#C9D0F2',
  'indigo-300': '#BCC6EB',
  'indigo-350': '#ADB9E8',
  'indigo-400': '#9DAAE6',
  'indigo-450': '#8E9CE3',
  'indigo-500': '#7C8DDF',
  'indigo-550': '#6E7CDA',
  'indigo-600': '#5F6DD3',
  'indigo-650': '#4D5FCC',
  'indigo-700': '#3143C4',
  'indigo-750': '#2E3CB4',
  'indigo-800': '#263295',
  'indigo-850': '#202479',
  'indigo-900': '#171957',
  'indigo-950': '#0F0F38',
};

export const azure = {
  'azure-50': '#F0F8FF',
  'azure-100': '#E4F2FF',
  'azure-150': '#D4EBFF',
  'azure-200': '#C9E1F9',
  'azure-250': '#B8D7F1',
  'azure-300': '#AACCE9',
  'azure-350': '#96C0E5',
  'azure-400': '#7EB4E3',
  'azure-450': '#67A7E0',
  'azure-500': '#4D9ADB',
  'azure-550': '#348AD6',
  'azure-600': '#267DC7',
  'azure-650': '#1A70B4',
  'azure-700': '#0A5B98',
  'azure-750': '#0A518C',
  'azure-800': '#094373',
  'azure-850': '#08345C',
  'azure-900': '#072544',
  'azure-950': '#04172B',
};

export const lagoon = {
  'lagoon-50': '#E7FBFF',
  'lagoon-100': '#D9F6FC',
  'lagoon-150': '#D1EEF4',
  'lagoon-200': '#BEE6EE',
  'lagoon-250': '#9EDFEC',
  'lagoon-300': '#81D7E7',
  'lagoon-350': '#64CCDF',
  'lagoon-400': '#4CBED2',
  'lagoon-450': '#3FB3C7',
  'lagoon-500': '#30A4B8',
  'lagoon-550': '#2196A8',
  'lagoon-600': '#128899',
  'lagoon-650': '#04798A',
  'lagoon-700': '#006A78',
  'lagoon-750': '#005A67',
  'lagoon-800': '#044A54',
  'lagoon-850': '#093A41',
  'lagoon-900': '#0D282D',
  'lagoon-950': '#01171C',
};

export const sage = {
  'sage-50': '#E6FDF1',
  'sage-100': '#E0F7EA',
  'sage-150': '#CCF2DE',
  'sage-200': '#B1EECF',
  'sage-250': '#99E6C1',
  'sage-300': '#8DDEB7',
  'sage-350': '#78D0A6',
  'sage-400': '#67C498',
  'sage-450': '#54B88A',
  'sage-500': '#3BAA7A',
  'sage-550': '#2B9D6E',
  'sage-600': '#1A8E61',
  'sage-650': '#078055',
  'sage-700': '#00704A',
  'sage-750': '#005F3E',
  'sage-800': '#004E32',
  'sage-850': '#003E27',
  'sage-900': '#072B1C',
  'sage-950': '#03190E',
};

export const UI_THEMES = {
  LIGHT: 'light',
  HIGH_CONTRAST: 'high-contrast',
  QA: 'qa',
  BRAND: 'brand',
} as const;

export type UITheme = (typeof UI_THEMES)[keyof typeof UI_THEMES];

export type SemanticColor =
  | 'background-black'
  | 'background-darker'
  | 'background-dark'
  | 'background-medium'
  | 'background-light'
  | 'background-lighter'
  | 'background-lightest'
  | 'background-white'
  | 'background-default'
  | 'background-hover'
  | 'background-texture'
  | 'background-info-text'
  | 'border-darker'
  | 'border-dark'
  | 'border-medium'
  | 'border-medium-light'
  | 'border-light'
  | 'border-active'
  | 'button-primary-pressed'
  | 'button-primary-hover'
  | 'button-primary-default'
  | 'button-secondary-pressed'
  | 'button-secondary-hover'
  | 'button-secondary-default'
  | 'button-warning-pressed'
  | 'button-warning-hover'
  | 'button-warning-default'
  | 'button-disabled-default'
  | 'button-tertiary-pressed'
  | 'button-tertiary-hover'
  | 'button-tertiary-default'
  | 'font-primary'
  | 'font-secondary'
  | 'font-placeholder'
  | 'font-white'
  | 'feedback-toast-information'
  | 'feedback-toast-success'
  | 'feedback-toast-warning'
  | 'feedback-toast-error'
  | 'viz-neutral'
  | 'viz-seq-red-1'
  | 'viz-seq-red-2'
  | 'viz-seq-red-3'
  | 'viz-seq-red-4'
  | 'viz-seq-red-5'
  | 'viz-seq-red-6'
  | 'viz-seq-red-7'
  | 'viz-seq-red-8'
  | 'viz-seq-red-9'
  | 'viz-seq-red-10'
  | 'feedback-success'
  | 'feedback-error'
  | 'feedback-warning'
  | 'feedback-info'
  | 'feedback-success-bg'
  | 'feedback-error-bg'
  | 'feedback-warning-bg'
  | 'feedback-info-bg'
  | 'feedback-success-foreground'
  | 'feedback-error-foreground'
  | 'feedback-warning-foreground'
  | 'feedback-info-foreground'
  | 'viz-cat-1-primary'
  | 'viz-cat-1-secondary'
  | 'viz-cat-2-primary'
  | 'viz-cat-2-secondary'
  | 'viz-cat-3-primary'
  | 'viz-cat-3-secondary'
  | 'viz-cat-4-primary'
  | 'viz-cat-4-secondary'
  | 'viz-cat-5-primary'
  | 'viz-cat-5-secondary'
  | 'viz-cat-6-primary'
  | 'viz-cat-6-secondary'
  | 'viz-cat-7-primary'
  | 'viz-cat-7-secondary'
  | 'viz-cat-8-primary'
  | 'viz-cat-8-secondary'
  | 'viz-cat-9-primary'
  | 'viz-cat-9-secondary'
  | 'viz-cat-10-primary'
  | 'viz-cat-10-secondary'
  | 'agentic-workflow-action-primary'
  | 'agentic-workflow-action-secondary'
  | 'agentic-workflow-agent-primary'
  | 'agentic-workflow-agent-secondary'
  | 'agentic-workflow-condition-primary'
  | 'agentic-workflow-condition-secondary'
  | 'agentic-workflow-connection-primary'
  | 'agentic-workflow-connection-secondary'
  | 'agentic-workflow-delay-primary'
  | 'agentic-workflow-delay-secondary'
  | 'agentic-workflow-error-primary'
  | 'agentic-workflow-error-secondary'
  | 'agentic-workflow-loop-primary'
  | 'agentic-workflow-loop-secondary'
  | 'agentic-workflow-trigger-primary'
  | 'agentic-workflow-trigger-secondary'
  | 'agentic-workflow-variable-primary'
  | 'agentic-workflow-variable-secondary'
  | 'ai-system-bg'
  | 'ai-system-text'
  | 'ai-user-bg'
  | 'ai-user-text'
  | 'ai-gradient-start'
  | 'ai-gradient-end'
  | 'logo-primary'
  | 'code-highlight-surface'
  | 'code-highlight-text'
  | 'code-highlight-comment'
  | 'code-highlight-keyword'
  | 'code-highlight-entity'
  | 'code-highlight-constant'
  | 'code-highlight-string'
  | 'code-highlight-symbol'
  | 'code-highlight-tag'
  | 'code-highlight-list'
  | 'code-highlight-params'
  | 'code-highlight-added-text'
  | 'code-highlight-added-surface'
  | 'code-highlight-removed-text'
  | 'code-highlight-removed-surface';

export const semanticNames: Record<
  SemanticColor,
  Record<UITheme, PaletteColor>
> = {
  'background-black': {
    [UI_THEMES.LIGHT]: 'gray-1000',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-1000',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'neutral-1000',
  },
  'background-darker': {
    [UI_THEMES.LIGHT]: 'gray-800',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'neutral-700',
  },
  'background-dark': {
    [UI_THEMES.LIGHT]: 'gray-600',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-700',
    [UI_THEMES.QA]: 'magenta-600',
    [UI_THEMES.BRAND]: 'neutral-500',
  },
  'background-medium': {
    [UI_THEMES.LIGHT]: 'gray-500',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-600',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'neutral-300',
  },
  'background-light': {
    [UI_THEMES.LIGHT]: 'gray-300',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-400',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'neutral-75',
  },
  'background-lighter': {
    [UI_THEMES.LIGHT]: 'gray-200',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-300',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'neutral-50',
  },
  'background-lightest': {
    [UI_THEMES.LIGHT]: 'gray-100',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-300',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'neutral-25',
  },
  'background-white': {
    [UI_THEMES.LIGHT]: 'gray-50',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-50',
    [UI_THEMES.QA]: 'magenta-25',
    [UI_THEMES.BRAND]: 'neutral-0',
  },
  'background-default': {
    [UI_THEMES.LIGHT]: 'aliceblue-25',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-300',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'neutral-25',
  },
  'background-hover': {
    [UI_THEMES.LIGHT]: 'blue-100',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-200',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'azure-50',
  },
  'background-texture': {
    [UI_THEMES.LIGHT]: 'blue-100',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-200',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'deepteal-150',
  },
  'background-info-text': {
    [UI_THEMES.LIGHT]: 'aliceblue-25',
    [UI_THEMES.HIGH_CONTRAST]: 'aliceblue-25',
    [UI_THEMES.QA]: 'magenta-25',
    [UI_THEMES.BRAND]: 'lightmist-50',
  },
  'border-darker': {
    [UI_THEMES.LIGHT]: 'gray-800',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'neutral-650',
  },
  'border-dark': {
    [UI_THEMES.LIGHT]: 'gray-600',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-700',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'neutral-400',
  },
  'border-medium': {
    [UI_THEMES.LIGHT]: 'gray-500',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-600',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'neutral-250',
  },
  'border-medium-light': {
    [UI_THEMES.LIGHT]: 'gray-400',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-500',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'neutral-150',
  },
  'border-light': {
    [UI_THEMES.LIGHT]: 'gray-300',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-400',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'neutral-50',
  },
  'border-active': {
    [UI_THEMES.LIGHT]: 'blue-500',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-600',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'azure-600',
  },
  'button-primary-pressed': {
    [UI_THEMES.LIGHT]: 'teal-700',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'deepteal-800',
  },
  'button-primary-hover': {
    [UI_THEMES.LIGHT]: 'teal-500',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-600',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'deepteal-700',
  },
  'button-primary-default': {
    [UI_THEMES.LIGHT]: 'teal-300',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-400',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'deepteal-650',
  },
  'button-secondary-pressed': {
    [UI_THEMES.LIGHT]: 'blue-800',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'azure-800',
  },
  'button-secondary-hover': {
    [UI_THEMES.LIGHT]: 'blue-700',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'azure-700',
  },
  'button-secondary-default': {
    [UI_THEMES.LIGHT]: 'blue-500',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-600',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'azure-650',
  },
  'button-warning-pressed': {
    [UI_THEMES.LIGHT]: 'red-700',
    [UI_THEMES.HIGH_CONTRAST]: 'red-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-800',
  },
  'button-warning-hover': {
    [UI_THEMES.LIGHT]: 'red-500',
    [UI_THEMES.HIGH_CONTRAST]: 'red-600',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'clay-700',
  },
  'button-warning-default': {
    [UI_THEMES.LIGHT]: 'red-300',
    [UI_THEMES.HIGH_CONTRAST]: 'red-400',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'clay-600',
  },
  'button-disabled-default': {
    [UI_THEMES.LIGHT]: 'gray-500',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-600',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'neutral-300',
  },
  'button-tertiary-pressed': {
    [UI_THEMES.LIGHT]: 'gray-900',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-900',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'neutral-850',
  },
  'button-tertiary-hover': {
    [UI_THEMES.LIGHT]: 'gray-800',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-800',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'neutral-700',
  },
  'button-tertiary-default': {
    [UI_THEMES.LIGHT]: 'gray-600',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-700',
    [UI_THEMES.QA]: 'magenta-600',
    [UI_THEMES.BRAND]: 'neutral-550',
  },
  'font-primary': {
    [UI_THEMES.LIGHT]: 'gray-800',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'neutral-800',
  },
  'font-secondary': {
    [UI_THEMES.LIGHT]: 'gray-700',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'neutral-700',
  },
  'font-placeholder': {
    [UI_THEMES.LIGHT]: 'gray-600',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'neutral-550',
  },
  'font-white': {
    [UI_THEMES.LIGHT]: 'gray-50',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-50',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'neutral-0',
  },
  'feedback-toast-information': {
    [UI_THEMES.LIGHT]: 'blue-500',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-600',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'azure-650',
  },
  'feedback-toast-success': {
    [UI_THEMES.LIGHT]: 'teal-300',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-400',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'sage-500',
  },
  'feedback-toast-warning': {
    [UI_THEMES.LIGHT]: 'orange-300',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-700',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'yolk-450',
  },
  'feedback-toast-error': {
    [UI_THEMES.LIGHT]: 'red-300',
    [UI_THEMES.HIGH_CONTRAST]: 'red-400',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'clay-500',
  },
  'viz-neutral': {
    [UI_THEMES.LIGHT]: 'gray-500',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-500',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'neutral-200',
  },
  'viz-seq-red-1': {
    [UI_THEMES.LIGHT]: 'red-900',
    [UI_THEMES.HIGH_CONTRAST]: 'red-900',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'clay-900',
  },
  'viz-seq-red-2': {
    [UI_THEMES.LIGHT]: 'red-800',
    [UI_THEMES.HIGH_CONTRAST]: 'red-800',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'clay-850',
  },
  'viz-seq-red-3': {
    [UI_THEMES.LIGHT]: 'red-700',
    [UI_THEMES.HIGH_CONTRAST]: 'red-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-800',
  },
  'viz-seq-red-4': {
    [UI_THEMES.LIGHT]: 'red-600',
    [UI_THEMES.HIGH_CONTRAST]: 'red-600',
    [UI_THEMES.QA]: 'magenta-600',
    [UI_THEMES.BRAND]: 'clay-700',
  },
  'viz-seq-red-5': {
    [UI_THEMES.LIGHT]: 'red-500',
    [UI_THEMES.HIGH_CONTRAST]: 'red-500',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'clay-600',
  },
  'viz-seq-red-6': {
    [UI_THEMES.LIGHT]: 'red-400',
    [UI_THEMES.HIGH_CONTRAST]: 'red-400',
    [UI_THEMES.QA]: 'magenta-400',
    [UI_THEMES.BRAND]: 'clay-550',
  },
  'viz-seq-red-7': {
    [UI_THEMES.LIGHT]: 'red-300',
    [UI_THEMES.HIGH_CONTRAST]: 'red-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'clay-450',
  },
  'viz-seq-red-8': {
    [UI_THEMES.LIGHT]: 'red-200',
    [UI_THEMES.HIGH_CONTRAST]: 'red-200',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'clay-300',
  },
  'viz-seq-red-9': {
    [UI_THEMES.LIGHT]: 'red-100',
    [UI_THEMES.HIGH_CONTRAST]: 'red-100',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'clay-200',
  },
  'viz-seq-red-10': {
    [UI_THEMES.LIGHT]: 'red-50',
    [UI_THEMES.HIGH_CONTRAST]: 'red-50',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'clay-100',
  },
  'feedback-success': {
    [UI_THEMES.LIGHT]: 'teal-300',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-400',
    [UI_THEMES.QA]: 'magenta-400',
    [UI_THEMES.BRAND]: 'sage-500',
  },
  'feedback-error': {
    [UI_THEMES.LIGHT]: 'red-500',
    [UI_THEMES.HIGH_CONTRAST]: 'red-600',
    [UI_THEMES.QA]: 'magenta-400',
    [UI_THEMES.BRAND]: 'clay-500',
  },
  'feedback-warning': {
    [UI_THEMES.LIGHT]: 'orange-500',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-600',
    [UI_THEMES.QA]: 'magenta-400',
    [UI_THEMES.BRAND]: 'yolk-450',
  },
  'feedback-info': {
    [UI_THEMES.LIGHT]: 'blue-500',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-600',
    [UI_THEMES.QA]: 'magenta-400',
    [UI_THEMES.BRAND]: 'azure-650',
  },
  'feedback-success-bg': {
    [UI_THEMES.LIGHT]: 'green-50',
    [UI_THEMES.HIGH_CONTRAST]: 'green-100',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'sage-50',
  },
  'feedback-error-bg': {
    [UI_THEMES.LIGHT]: 'red-50',
    [UI_THEMES.HIGH_CONTRAST]: 'red-100',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'clay-50',
  },
  'feedback-warning-bg': {
    [UI_THEMES.LIGHT]: 'orange-40',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-50',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'yolk-50',
  },
  'feedback-info-bg': {
    [UI_THEMES.LIGHT]: 'blue-50',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-100',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'azure-50',
  },
  'feedback-success-foreground': {
    [UI_THEMES.LIGHT]: 'green-800',
    [UI_THEMES.HIGH_CONTRAST]: 'green-900',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'sage-700',
  },
  'feedback-error-foreground': {
    [UI_THEMES.LIGHT]: 'red-900',
    [UI_THEMES.HIGH_CONTRAST]: 'red-900',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-700',
  },
  'feedback-warning-foreground': {
    [UI_THEMES.LIGHT]: 'orange-900',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-900',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'yolk-700',
  },
  'feedback-info-foreground': {
    [UI_THEMES.LIGHT]: 'blue-800',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-900',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'azure-800',
  },
  'viz-cat-1-primary': {
    [UI_THEMES.LIGHT]: 'green-700',
    [UI_THEMES.HIGH_CONTRAST]: 'green-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'sage-500',
  },
  'viz-cat-1-secondary': {
    [UI_THEMES.LIGHT]: 'green-300',
    [UI_THEMES.HIGH_CONTRAST]: 'green-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'sage-400',
  },
  'viz-cat-2-primary': {
    [UI_THEMES.LIGHT]: 'blue-800',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'petrol-700',
  },
  'viz-cat-2-secondary': {
    [UI_THEMES.LIGHT]: 'blue-300',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'petrol-400',
  },
  'viz-cat-3-primary': {
    [UI_THEMES.LIGHT]: 'orange-500',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-500',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'yolk-350',
  },
  'viz-cat-3-secondary': {
    [UI_THEMES.LIGHT]: 'orange-100',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-100',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'yolk-150',
  },
  'viz-cat-4-primary': {
    [UI_THEMES.LIGHT]: 'purple-700',
    [UI_THEMES.HIGH_CONTRAST]: 'purple-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'indigo-750',
  },
  'viz-cat-4-secondary': {
    [UI_THEMES.LIGHT]: 'purple-300',
    [UI_THEMES.HIGH_CONTRAST]: 'purple-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'indigo-400',
  },
  'viz-cat-5-primary': {
    [UI_THEMES.LIGHT]: 'aqua-600',
    [UI_THEMES.HIGH_CONTRAST]: 'aqua-600',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'lagoon-600',
  },
  'viz-cat-5-secondary': {
    [UI_THEMES.LIGHT]: 'aqua-300',
    [UI_THEMES.HIGH_CONTRAST]: 'aqua-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'lagoon-250',
  },
  'viz-cat-6-primary': {
    [UI_THEMES.LIGHT]: 'tangerine-500',
    [UI_THEMES.HIGH_CONTRAST]: 'tangerine-500',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-400',
  },
  'viz-cat-6-secondary': {
    [UI_THEMES.LIGHT]: 'tangerine-300',
    [UI_THEMES.HIGH_CONTRAST]: 'tangerine-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'yolk-300',
  },
  'viz-cat-7-primary': {
    [UI_THEMES.LIGHT]: 'red-700',
    [UI_THEMES.HIGH_CONTRAST]: 'red-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-600',
  },
  'viz-cat-7-secondary': {
    [UI_THEMES.LIGHT]: 'red-200',
    [UI_THEMES.HIGH_CONTRAST]: 'red-200',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'clay-350',
  },
  'viz-cat-8-primary': {
    [UI_THEMES.LIGHT]: 'magenta-600',
    [UI_THEMES.HIGH_CONTRAST]: 'magenta-600',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'indigo-500',
  },
  'viz-cat-8-secondary': {
    [UI_THEMES.LIGHT]: 'magenta-300',
    [UI_THEMES.HIGH_CONTRAST]: 'magenta-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'indigo-200',
  },
  'viz-cat-9-primary': {
    [UI_THEMES.LIGHT]: 'brown-600',
    [UI_THEMES.HIGH_CONTRAST]: 'brown-600',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'yolk-750',
  },
  'viz-cat-9-secondary': {
    [UI_THEMES.LIGHT]: 'brown-300',
    [UI_THEMES.HIGH_CONTRAST]: 'brown-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'yolk-550',
  },
  'viz-cat-10-primary': {
    [UI_THEMES.LIGHT]: 'warmgray-700',
    [UI_THEMES.HIGH_CONTRAST]: 'warmgray-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'neutral-550',
  },
  'viz-cat-10-secondary': {
    [UI_THEMES.LIGHT]: 'warmgray-300',
    [UI_THEMES.HIGH_CONTRAST]: 'warmgray-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'neutral-300',
  },
  'agentic-workflow-action-primary': {
    [UI_THEMES.LIGHT]: 'green-800',
    [UI_THEMES.HIGH_CONTRAST]: 'green-50',
    [UI_THEMES.QA]: 'magenta-600',
    [UI_THEMES.BRAND]: 'sage-700',
  },
  'agentic-workflow-action-secondary': {
    [UI_THEMES.LIGHT]: 'green-100',
    [UI_THEMES.HIGH_CONTRAST]: 'green-600',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'sage-100',
  },
  'agentic-workflow-agent-primary': {
    [UI_THEMES.LIGHT]: 'teal-900',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-50',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'deepteal-850',
  },
  'agentic-workflow-agent-secondary': {
    [UI_THEMES.LIGHT]: 'teal-50',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-700',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'deepteal-50',
  },
  'agentic-workflow-condition-primary': {
    [UI_THEMES.LIGHT]: 'purple-700',
    [UI_THEMES.HIGH_CONTRAST]: 'purple-25',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'indigo-750',
  },
  'agentic-workflow-condition-secondary': {
    [UI_THEMES.LIGHT]: 'purple-100',
    [UI_THEMES.HIGH_CONTRAST]: 'purple-600',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'indigo-250',
  },
  'agentic-workflow-connection-primary': {
    [UI_THEMES.LIGHT]: 'warmgray-700',
    [UI_THEMES.HIGH_CONTRAST]: 'warmgray-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'neutral-500',
  },
  'agentic-workflow-connection-secondary': {
    [UI_THEMES.LIGHT]: 'warmgray-300',
    [UI_THEMES.HIGH_CONTRAST]: 'warmgray-300',
    [UI_THEMES.QA]: 'magenta-300',
    [UI_THEMES.BRAND]: 'neutral-200',
  },
  'agentic-workflow-delay-primary': {
    [UI_THEMES.LIGHT]: 'orange-900',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-40',
    [UI_THEMES.QA]: 'magenta-500',
    [UI_THEMES.BRAND]: 'yolk-700',
  },
  'agentic-workflow-delay-secondary': {
    [UI_THEMES.LIGHT]: 'orange-50',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-700',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'yolk-150',
  },
  'agentic-workflow-error-primary': {
    [UI_THEMES.LIGHT]: 'red-700',
    [UI_THEMES.HIGH_CONTRAST]: 'red-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-700',
  },
  'agentic-workflow-error-secondary': {
    [UI_THEMES.LIGHT]: 'red-50',
    [UI_THEMES.HIGH_CONTRAST]: 'red-100',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'clay-150',
  },
  'agentic-workflow-loop-primary': {
    [UI_THEMES.LIGHT]: 'brown-800',
    [UI_THEMES.HIGH_CONTRAST]: 'brown-25',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'yolk-850',
  },
  'agentic-workflow-loop-secondary': {
    [UI_THEMES.LIGHT]: 'brown-100',
    [UI_THEMES.HIGH_CONTRAST]: 'brown-600',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'yolk-100',
  },
  'agentic-workflow-trigger-primary': {
    [UI_THEMES.LIGHT]: 'blue-900',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-100',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'azure-850',
  },
  'agentic-workflow-trigger-secondary': {
    [UI_THEMES.LIGHT]: 'blue-200',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-900',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'azure-200',
  },
  'agentic-workflow-variable-primary': {
    [UI_THEMES.LIGHT]: 'teal-900',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-50',
    [UI_THEMES.QA]: 'magenta-600',
    [UI_THEMES.BRAND]: 'deepteal-750',
  },
  'agentic-workflow-variable-secondary': {
    [UI_THEMES.LIGHT]: 'teal-100',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-500',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'deepteal-200',
  },
  'ai-system-bg': {
    [UI_THEMES.LIGHT]: 'green-150',
    [UI_THEMES.HIGH_CONTRAST]: 'green-150',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'sage-50',
  },
  'ai-system-text': {
    [UI_THEMES.LIGHT]: 'green-1000',
    [UI_THEMES.HIGH_CONTRAST]: 'green-1000',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'sage-900',
  },
  'ai-user-bg': {
    [UI_THEMES.LIGHT]: 'green-1000',
    [UI_THEMES.HIGH_CONTRAST]: 'green-1000',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'deepteal-800',
  },
  'ai-user-text': {
    [UI_THEMES.LIGHT]: 'gray-50',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-50',
    [UI_THEMES.QA]: 'magenta-100',
    [UI_THEMES.BRAND]: 'neutral-0',
  },
  'ai-gradient-start': {
    [UI_THEMES.LIGHT]: 'aqua-150',
    [UI_THEMES.HIGH_CONTRAST]: 'aqua-150',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'lagoon-100',
  },
  'ai-gradient-end': {
    [UI_THEMES.LIGHT]: 'green-150',
    [UI_THEMES.HIGH_CONTRAST]: 'green-150',
    [UI_THEMES.QA]: 'magenta-200',
    [UI_THEMES.BRAND]: 'sage-100',
  },
  'logo-primary': {
    [UI_THEMES.LIGHT]: 'brand-100',
    [UI_THEMES.HIGH_CONTRAST]: 'brand-100',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'deepteal-750',
  },
  'code-highlight-surface': {
    [UI_THEMES.LIGHT]: 'gray-50',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-50',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'neutral-0',
  },
  'code-highlight-text': {
    [UI_THEMES.LIGHT]: 'gray-900',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'neutral-850',
  },
  'code-highlight-comment': {
    [UI_THEMES.LIGHT]: 'gray-700',
    [UI_THEMES.HIGH_CONTRAST]: 'gray-700',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'neutral-500',
  },
  'code-highlight-keyword': {
    [UI_THEMES.LIGHT]: 'red-800',
    [UI_THEMES.HIGH_CONTRAST]: 'red-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'clay-800',
  },
  'code-highlight-entity': {
    [UI_THEMES.LIGHT]: 'purple-700',
    [UI_THEMES.HIGH_CONTRAST]: 'purple-800',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'petrol-800',
  },
  'code-highlight-constant': {
    [UI_THEMES.LIGHT]: 'blue-800',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-900',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'petrol-700',
  },
  'code-highlight-string': {
    [UI_THEMES.LIGHT]: 'blue-900',
    [UI_THEMES.HIGH_CONTRAST]: 'blue-950',
    [UI_THEMES.QA]: 'magenta-900',
    [UI_THEMES.BRAND]: 'deepteal-800',
  },
  'code-highlight-symbol': {
    [UI_THEMES.LIGHT]: 'tangerine-650',
    [UI_THEMES.HIGH_CONTRAST]: 'tangerine-700',
    [UI_THEMES.QA]: 'magenta-600',
    [UI_THEMES.BRAND]: 'yolk-600',
  },
  'code-highlight-tag': {
    [UI_THEMES.LIGHT]: 'green-800',
    [UI_THEMES.HIGH_CONTRAST]: 'green-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'sage-700',
  },
  'code-highlight-list': {
    [UI_THEMES.LIGHT]: 'orange-900',
    [UI_THEMES.HIGH_CONTRAST]: 'orange-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'yolk-800',
  },
  'code-highlight-params': {
    [UI_THEMES.LIGHT]: 'teal-800',
    [UI_THEMES.HIGH_CONTRAST]: 'teal-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'deepteal-700',
  },
  'code-highlight-added-text': {
    [UI_THEMES.LIGHT]: 'green-800',
    [UI_THEMES.HIGH_CONTRAST]: 'green-900',
    [UI_THEMES.QA]: 'magenta-800',
    [UI_THEMES.BRAND]: 'sage-800',
  },
  'code-highlight-added-surface': {
    [UI_THEMES.LIGHT]: 'green-50',
    [UI_THEMES.HIGH_CONTRAST]: 'green-100',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'sage-50',
  },
  'code-highlight-removed-text': {
    [UI_THEMES.LIGHT]: 'red-900',
    [UI_THEMES.HIGH_CONTRAST]: 'red-900',
    [UI_THEMES.QA]: 'magenta-700',
    [UI_THEMES.BRAND]: 'clay-850',
  },
  'code-highlight-removed-surface': {
    [UI_THEMES.LIGHT]: 'red-50',
    [UI_THEMES.HIGH_CONTRAST]: 'red-100',
    [UI_THEMES.QA]: 'magenta-50',
    [UI_THEMES.BRAND]: 'clay-50',
  },
};

export type SemanticColorName = keyof typeof semanticNames;

export const aliases = Object.keys(semanticNames).reduce((acc, key) => {
  return {
    ...acc,
    [key]: `var(--color-${key})`,
  };
}, {});

export const spacing = {
  'spacer-5': '0.313em',
  'spacer-8': '0.5em',
  'spacer-10': '0.625em',
  'spacer-12': '0.75em',
  'spacer-15': '0.938em',
  'spacer-20': '1.25em',
  'spacer-25': '1.563em',
  'spacer-30': '1.875em',
};

export const fontSize = {
  xs: '0.625em',
  sm: '0.75em',
  md: '0.875em',
  base: '1em',
  lg: '1.125em',
  xl: '1.25em',
  '2xl': '1.5em',
  '3xl': '2.125em',
  '4xl': '3em',
  'size-reset': '16px',
  unset: 'unset',
};

export const semanticNamesByTheme = Object.keys(semanticNames).reduce(
  (acc, key) => {
    const alias = key as SemanticColorName;
    Object.keys(semanticNames[alias]).forEach((key) => {
      const theme = key as UITheme;
      acc[theme][alias] = semanticNames[alias][theme];
    });
    return acc;
  },
  {
    [UI_THEMES.LIGHT]: {},
    [UI_THEMES.HIGH_CONTRAST]: {},
    [UI_THEMES.QA]: {},
    [UI_THEMES.BRAND]: {},
  } as Record<UITheme, Record<SemanticColorName, string>>
);

// Expanding scroll bars have a z-index of 2 currently
// in react-app, so basic menus need to start at 3,
// also we need ScrollTopButton to appear above Testing Mode badge
// but below modal backdrop
export const zIndex = {
  menu: 3,
  scrollTop: 1039,
};
